import EditableInput from "@/components/ui/editable-input";
import { MapPin, Building, User, Edit, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useRef, useEffect, useState } from "react";

interface BillingData {
  billing_address: string;
  is_company: boolean;
  vat_number: string;
  fiscal_code: string;
}

interface BillingSectionProps {
  billingData: BillingData;
  onBillingDataChange: (data: BillingData) => void;
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
}

const BillingSection = ({ 
  billingData, 
  onBillingDataChange, 
  isEditing = false,
  onEditToggle,
  onCancelEdit,
  onSave,
  isSaving = false
}: BillingSectionProps) => {
  const formRef = useRef<HTMLFormElement>(null);

  const handleInputChange = (field: keyof BillingData) => (value: string | boolean) => {
    onBillingDataChange({
      ...billingData,
      [field]: value
    });
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && onSave) {
      onSave();
    }
  };

  // Gestione tasto Invio
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isEditing && onSave) {
        e.preventDefault();
        onSave();
      }
    };

    if (isEditing) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, onSave]);

  return (
    <form ref={formRef} onSubmit={handleFormSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-2">
            <Building className="h-5 w-5" />
            Fatturazione
          </h3>
          <p className="text-sm text-gray-600">
            Gestisci le informazioni di fatturazione
          </p>
        </div>
        {onEditToggle && onCancelEdit && (
          <div className="flex gap-2 min-h-[40px] items-center">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancelEdit}
                  className="flex items-center gap-2"
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                  Annulla
                </Button>
                {onSave && (
                  <Button
                    type="submit"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2"
                  >
                    {isSaving ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isSaving ? 'Salvando...' : 'Salva'}
                  </Button>
                )}
              </>
            ) : (
              <Button
                type="button"
                onClick={onEditToggle}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Modifica
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Billing Address */}
      <EditableInput
        label="Indirizzo di Fatturazione *"
        value={billingData.billing_address}
        icon={MapPin}
        isEditing={isEditing}
        onChange={(value) => handleInputChange('billing_address')(value)}
        placeholder="Via Roma 123, 00100 Roma, Italia"
      />

      {/* Company/Individual Switch */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">
          Tipo di Fatturazione
        </Label>
        {isEditing ? (
          <div className="flex items-center space-x-3">
            <Switch
              id="company-mode"
              checked={billingData.is_company}
              onCheckedChange={(checked) => handleInputChange('is_company')(checked)}
              disabled={isSaving}
            />
            <Label htmlFor="company-mode" className="text-sm text-gray-700">
              {billingData.is_company ? 'Azienda' : 'Privato'}
            </Label>
          </div>
        ) : (
          <div className="min-h-[34px] flex items-center">
            <div className="flex-1 text-gray-900">
              {billingData.is_company ? 'Azienda' : 'Privato'}
            </div>
          </div>
        )}
        {!isEditing && <div className="h-px bg-gray-300 w-full"></div>}
      </div>

      {/* VAT Number or Fiscal Code */}
      {billingData.is_company ? (
        <EditableInput
          label="Partita IVA *"
          value={billingData.vat_number}
          icon={Building}
          isEditing={isEditing}
          onChange={(value) => handleInputChange('vat_number')(value.toUpperCase())}
          placeholder="12345678901"
          maxLength={11}
          className="font-mono text-sm"
        />
      ) : (
        <EditableInput
          label="Codice Fiscale *"
          value={billingData.fiscal_code}
          icon={User}
          isEditing={isEditing}
          onChange={(value) => handleInputChange('fiscal_code')(value.toUpperCase())}
          placeholder="RSSMRA80A01H501U"
          maxLength={16}
          className="font-mono text-sm"
        />
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Building className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-blue-900">
              Informazioni Fatturazione
            </h4>
            <p className="text-sm text-blue-700">
              {billingData.is_company 
                ? "Le informazioni aziendali verranno utilizzate per la fatturazione elettronica."
                : "Il codice fiscale è necessario per la fatturazione elettronica."
              }
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default BillingSection;
